import { ErrorTooltip, InfoTooltip, LoadingCell } from './components/index.js';
import {
  type ColDef,
  type ITooltipParams,
  type ValueGetterFunc,
  type ValueGetterParams,
} from './enterprise.js';

export type TooltipDetails = {
  type: 'error' | 'info';
  value: string | null | undefined;
};

export type ColumnOptions<T = unknown, U = unknown> = Partial<ColDef<T, U>>;

type ColumnOptionsWithErrorHandling<T = unknown, U = unknown> = ColumnOptions<
  T,
  U
> & {
  valueGetter: ValueGetterFunc<T, U>;
};

/**
 * Decorator for a column definition to add error handling.
 * Includes:
 * - CellError-based cell class rules
 * - InfoTooltip handling for errors and info
 * - Value getter to display errorValue if exist
 *
 * @template T - The type of the row data.
 * @param options - Optional column overrides.
 * @returns A ColDef object ready to use in ag-Grid.
 */
export function colDefWithErrorHandling<T = object, U = string>(
  options: ColumnOptionsWithErrorHandling<T, U>,
): ColDef<T> {
  const {
    cellClassRules = {},
    tooltipValueGetter,
    valueGetter,
    ...rest
  } = options;

  const defaultColDef: ColDef<T> = {
    loadingCellRenderer: LoadingCell,
    cellClassRules: {
      '!border-red-500': (params) => !!params.context?.getCellError(params),
      ...cellClassRules,
    },
    tooltipComponent: (params: ITooltipParams<T> & TooltipDetails) => {
      const Wrapper = params.type === 'error' ? ErrorTooltip : InfoTooltip;

      return <Wrapper>{params.value}</Wrapper>;
    },
    tooltipComponentParams: (
      params: ITooltipParams<T, U, BaseContext<T>>,
    ): TooltipDetails => {
      const err = params.context?.getCellError(params);
      return {
        type: err ? 'error' : 'info',
        value: typeof params.value === 'string' ? params.value : '',
      };
    },
    tooltipValueGetter: (
      params: ITooltipParams<T>,
    ): TooltipDetails['value'] => {
      const err = params.context?.getCellError(params);
      if (err) {
        return err.message;
      }
      return tooltipValueGetter?.(params);
    },
    valueGetter: (params: ValueGetterParams<T, U>) => {
      const err = params.context?.getCellError(params);

      if (err) {
        return err.errorValue;
      }

      return valueGetter(params);
    },
    ...rest,
  };

  return defaultColDef;
}

/**
 * Creates a default column definition with centralized configuration.
 * Includes:
 * - Loading cell renderer
 *
 * @template T - The type of the row data.
 * @param options - Optional column overrides.
 * @returns A ColDef object ready to use in ag-Grid.
 */
export function createDefaultColumn<T = object, U = string>(
  options: ColumnOptions<T, U>,
): ColDef<T> {
  const defaultColDef: ColDef<T> = {
    loadingCellRenderer: LoadingCell,
    ...options,
  };

  return defaultColDef;
}

type CellError = {
  errorValue: string | string[] | number | null;
  message: string;
};

export type BaseContext<T> = {
  getCellError: (params: ITooltipParams<T>) => CellError | null;
  errors: { [rowId: string]: { [colId: string]: CellError } };
  checkForGridErrors: () => boolean;
};
/**
 * Creates a context object for ag-Grid cells that includes error handling.
 *
 * @template T - The type of the row data.
 * @template U - Additional properties to include in the context.
 * @returns A context object containing `getCellError` and any extra options.
 */
export function createContext<T, U extends object = object>(
  options: U = {} as U,
): BaseContext<T> & U {
  const context = {
    /**
     * Returns the error object for a given cell, or null if no error exists.
     *
     * @param params - The tooltip  parameters from ag-Grid.
     * @returns An error object if present, otherwise null.
     */
    getCellError: (params: ITooltipParams<T>): CellError | null => {
      const colId =
        (params.colDef as ColDef<T>)?.colId ??
        (params.column as ColDef<T>)?.colId;

      const rowId = params.node?.id;

      return colId && rowId ? params.context.errors?.[rowId]?.[colId] : null;
    },
    errors: {},
    checkForGridErrors() {
      const errors = this.errors as BaseContext<T>['errors'];
      return Object.values(errors).some(
        (rowErrors) => Object.keys(rowErrors).length > 0,
      );
    },
    ...options,
  };

  return context;
}
